import { Analytics } from '@vercel/analytics/react';
import { SpeedInsights } from '@vercel/speed-insights/react';
import { StrictMode } from 'react';
import { createRoot, hydrateRoot } from 'react-dom/client';

import { AppShell } from '@/app-shell';

// Self-hosted fonts (no Google Fonts CDN). Geist (Sans + Mono) is loaded via
// @font-face in globals.css from /public/fonts (Vercel's original cut). These
// are the remaining families from Fontsource — variable, weight-axis only.
import '@fontsource-variable/inter/standard.css';
import '@fontsource-variable/jetbrains-mono/wght.css';
import '@fontsource-variable/merriweather/standard.css';
import '@/styles/globals.css';
import { AppEnvMap, AppEnvironment } from '@/core/app-env';
import '@/lib/datadog';
import { initSentry } from '@/lib/sentry';
import { router } from '@/router';

initSentry();

const container = document.getElementById('app');
if (!container) {
  throw new Error('Root element #app not found');
}

function KillSwitch({ children }: { children: React.ReactNode }) {
  if (import.meta.env.VITE_KILL === 'true') {
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-4 p-8 text-center">
        <h1 className="text-2xl font-semibold">Service unavailable</h1>
        <p className="text-muted-foreground">
          We&rsquo;re temporarily down. Please check back shortly.
        </p>
      </div>
    );
  }
  return <>{children}</>;
}

const app = (
  <StrictMode>
    <KillSwitch>
      <AppShell router={router} />
      {AppEnvMap.Env === AppEnvironment.Prod && (
        <>
          <Analytics />
          <SpeedInsights />
        </>
      )}
    </KillSwitch>
  </StrictMode>
);

// Prerendered routes (marketing) ship real markup in #app — hydrate it so the
// build-time HTML is reused and the handoff is seamless. App routes serve the
// blank shell (#app empty), so there's nothing to hydrate; client-render to
// avoid a hydration mismatch.
if (container.hasChildNodes()) {
  hydrateRoot(container, app);
} else {
  createRoot(container).render(app);
}
